/**
 * Campos de formulário reusados pelos painéis do setor: lugar de trabalho
 * (subsetor + posto coerentes, a mesma regra do backend), colaborador,
 * prioridade e a busca de ambiente para ligar uma tarefa ou desmembrar peça.
 *
 * Na v3 tudo casa por `setorId` (id do cadastro), não mais pelo código fixo.
 */
import React, { useId, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";

import { normalizeText } from "../../../utils/data";
import { PRIORIDADES, ROTULO_PRIORIDADE } from "../dados/vocabulario";
import { colaboradoresDoSetor, postosDoSetor, subsetoresDoSetor } from "./modelo";
import { Ajuda, Campo, Campos, Chip, Lista } from "./styles";

const MAX_SUGESTOES = 8;

/**
 * Subsetor e posto do setor. Escolher um posto acerta o subsetor (o posto
 * mora num só); trocar o subsetor tira o posto que não é dele. Inativos só
 * aparecem se já estão escolhidos.
 */
export function CamposLugar({
  setorId,
  estado,
  subsetorId,
  postoId,
  onChange,
  desabilitado = false,
  rotuloFila = "Fila do setor",
}) {
  const subsetores = subsetoresDoSetor(estado?.subsetores, setorId, { incluirInativos: true }).filter(
    (s) => s.ativo !== false || s.id === subsetorId
  );
  const todosPostos = postosDoSetor(estado?.postos, setorId, { incluirInativos: true }).filter(
    (p) => p.ativo !== false || p.id === postoId
  );
  const postos = todosPostos.filter((p) => (p.subsetor_id || null) === (subsetorId || null));

  const mudarSubsetor = (novo) => {
    const posto = todosPostos.find((p) => p.id === postoId);
    const manterPosto = posto && (posto.subsetor_id || null) === (novo || null);
    onChange({ subsetorId: novo || null, postoId: manterPosto ? postoId : null });
  };
  const mudarPosto = (novo) => {
    const posto = todosPostos.find((p) => p.id === novo);
    onChange({ subsetorId: posto ? posto.subsetor_id || null : subsetorId || null, postoId: novo || null });
  };

  return (
    <Campos $min={180}>
      <Campo>
        Subsetor
        <select value={subsetorId || ""} onChange={(e) => mudarSubsetor(e.target.value)} disabled={desabilitado}>
          <option value="">{rotuloFila}</option>
          {subsetores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nome}
              {s.ativo === false ? " (desativado)" : ""}
            </option>
          ))}
        </select>
      </Campo>
      <Campo>
        Posto de trabalho
        <select value={postoId || ""} onChange={(e) => mudarPosto(e.target.value)} disabled={desabilitado}>
          <option value="">Sem posto</option>
          {postos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nome}
              {p.ativo === false ? " (desativado)" : ""}
            </option>
          ))}
        </select>
      </Campo>
    </Campos>
  );
}

/** Colaborador: os do setor primeiro, os de outros setores depois. */
export function CampoColaborador({ setorId, colaboradores, valor, onChange, desabilitado = false, rotulo = "Colaborador" }) {
  const doSetor = colaboradoresDoSetor(colaboradores, setorId);
  const idsDoSetor = new Set(doSetor.map((c) => c.id));
  const outros = (colaboradores || [])
    .filter((c) => !idsDoSetor.has(c.id) && (c.ativo !== false || c.id === valor))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  return (
    <Campo>
      {rotulo}
      <select value={valor || ""} onChange={(e) => onChange(e.target.value || null)} disabled={desabilitado}>
        <option value="">Sem colaborador</option>
        {doSetor.length > 0 && (
          <optgroup label="Deste setor">
            {doSetor.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </optgroup>
        )}
        {outros.length > 0 && (
          <optgroup label="Outros setores">
            {outros.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
                {c.ativo === false ? " (desativado)" : ""}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </Campo>
  );
}

/**
 * Prioridade (normal, alta, urgente). `ajuda` diz de onde veio a que está
 * aparecendo — herdada do ambiente ou estimada pelo prazo (ADR-0059: rótulo
 * tem que dizer a régua).
 */
export function CampoPrioridade({ valor, onChange, desabilitado = false, ajuda = "", rotulo = "Prioridade" }) {
  const idAjuda = useId();
  return (
    <div>
      <Campo>
        {rotulo}
        <select
          value={valor || ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={desabilitado}
          aria-describedby={ajuda ? idAjuda : undefined}
        >
          {PRIORIDADES.map((p) => (
            <option key={p} value={p}>{ROTULO_PRIORIDADE[p]}</option>
          ))}
        </select>
      </Campo>
      {ajuda && <Ajuda id={idAjuda}>{ajuda}</Ajuda>}
    </div>
  );
}

/**
 * Busca de ambiente (opcional) para ligar a tarefa: digita pedido, cliente
 * ou ambiente e escolhe. `valor` = demanda escolhida ou null.
 */
export function CampoAmbiente({ demandas, valor, onChange, desabilitado = false }) {
  const [busca, setBusca] = useState("");
  const sugestoes = useMemo(() => {
    const palavras = normalizeText(busca).split(" ").filter(Boolean);
    if (!palavras.length) return [];
    return (demandas || [])
      .filter((d) => d.chave)
      .filter((d) => {
        const texto = normalizeText([d.rotulo, d.cliente, d.lote].filter(Boolean).join(" "));
        return palavras.every((p) => texto.includes(p));
      })
      .slice(0, MAX_SUGESTOES);
  }, [busca, demandas]);

  if (valor) {
    return (
      <Campo as="div">
        Ambiente
        <div>
          <Chip type="button" $ativo onClick={() => onChange(null)} disabled={desabilitado} title="Tirar o ambiente">
            <span className="texto">{valor.rotulo}{valor.cliente ? ` · ${valor.cliente}` : ""}</span>
            <FiX size={12} aria-hidden="true" />
          </Chip>
        </div>
      </Campo>
    );
  }
  return (
    <Campo>
      Ambiente (opcional)
      <input
        type="search"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar pedido, cliente ou ambiente"
        disabled={desabilitado}
      />
      {sugestoes.length > 0 && (
        <Lista role="listbox" aria-label="Ambientes encontrados">
          {sugestoes.map((d) => (
            <li key={d.chaveLinha}>
              <button
                type="button"
                role="option"
                aria-selected="false"
                className="btn btn--ghost btn--sm"
                style={{ width: "100%", justifyContent: "flex-start" }}
                onClick={() => {
                  onChange(d);
                  setBusca("");
                }}
              >
                {d.rotulo}
                {d.cliente ? ` · ${d.cliente}` : ""}
              </button>
            </li>
          ))}
        </Lista>
      )}
    </Campo>
  );
}

/** "  Montagem   final " vira "Montagem final" (no onBlur, nunca no onChange). */
export function limparEspacos(texto) {
  return String(texto || "").replace(/\s+/g, " ").trim();
}
